import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface BuildingIconProps {
    variant: 1 | 2 | 3 | 4;
    className?: string;
    score?: number; // Optional scaling factor if needed
}

const BuildingIcon: React.FC<BuildingIconProps> = ({ variant, className = '' }) => {
    const { theme } = useTheme();

    // Define colors - Neutral Slate Palette (Professional/Premium)
    // Darker shades for definition, Lighter for contrast
    const c = {
        fill: theme === 'dark' ? '#94a3b8' : '#64748b',   // Base Walls (Slate 400/500)
        roof: theme === 'dark' ? '#475569' : '#334155',   // Roof (Slate 600/700)
        window: theme === 'dark' ? '#f1f5f9' : '#e2e8f0', // Windows (Slate 100/200) - Lit up
        detail: theme === 'dark' ? '#cbd5e1' : '#94a3b8', // Details
        shadow: theme === 'dark' ? '#334155' : '#1e293b'  // Deep shadow
    };

    // Size modulation: Ensure smaller buildings take up less space in the grid cell
    // to visually reinforce the score difference.
    let scale = 1;
    let translateY = 0;

    switch (variant) {
        case 1: scale = 1.1; translateY = 0; break;    // Max Size (Score > 60)
        case 2: scale = 0.95; translateY = 2; break;   // Large
        case 3: scale = 0.85; translateY = 3; break;   // Medium
        case 4: scale = 0.75; translateY = 4; break;   // Small (Score < 24)
    }

    const style = {
        // No specific filter here, we use fill colors directly
        transform: `scale(${scale}) translateY(${translateY}px)`,
        transformOrigin: 'bottom center',
        filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))', // Subtle depth
        transition: 'transform 0.3s ease'
    };

    // Render specific variant paths
    const renderPaths = () => {
        switch (variant) {
            case 1: // The Biggest Building (Two joined blocks)
                return (
                    <svg viewBox="0 0 139.5 111.86" className={className} style={style}>
                        <g>
                            {/* Left Tower Base */}
                            <polygon fill={c.roof} points="43.78 0 0 15.42 0 111.86 43.78 111.86 43.78 0" />
                            <polygon fill={c.roof} points="70.99 57.04 43.61 41.62 43.61 111.86 70.99 111.86 70.99 57.04" />

                            {/* Left Tower Windows */}
                            <rect fill={c.window} x="6.45" y="23.38" width="30.32" height="4.54" />
                            <rect fill={c.window} x="6.45" y="34.12" width="30.32" height="4.54" />
                            <rect fill={c.window} x="6.45" y="44.86" width="30.32" height="4.54" />
                            <rect fill={c.window} x="6.45" y="55.59" width="30.32" height="4.54" />
                            <rect fill={c.window} x="6.45" y="66.33" width="30.32" height="4.54" />
                            <rect fill={c.window} x="6.45" y="77.06" width="30.32" height="4.54" />
                            <rect fill={c.window} x="6.45" y="87.8" width="30.32" height="4.54" />
                            <rect fill={c.window} x="6.45" y="98.54" width="30.32" height="4.54" />

                            {/* Center Connectors */}
                            <rect fill={c.window} x="45.93" y="64.02" width="6.46" height="6.46" />
                            <rect fill={c.window} x="45.93" y="75.28" width="6.46" height="6.46" />
                            <rect fill={c.window} x="45.93" y="86.53" width="6.46" height="6.46" />
                            <rect fill={c.window} x="45.93" y="97.79" width="6.46" height="6.46" />
                            <rect fill={c.window} x="57.89" y="64.02" width="6.46" height="6.46" />
                            <rect fill={c.window} x="57.89" y="75.28" width="6.46" height="6.46" />
                            <rect fill={c.window} x="57.89" y="86.53" width="6.46" height="6.46" />
                            <rect fill={c.window} x="57.89" y="97.79" width="6.46" height="6.46" />

                            {/* Right Main Building */}
                            <rect fill={c.fill} x="67.48" y="9.87" width="72.02" height="8.71" />
                            {/* Right Grid Pattern */}
                            <path fill={c.fill} d="M67.48,21.56v90.3h32.73v-13.09h7.84v13.09h31.45V21.56h-72.02ZM84.74,31.87h5.85v8.94h-5.85v-8.94ZM84.74,44.37h5.85v8.94h-5.85v-8.94ZM84.74,56.86h5.85v8.94h-5.85v-8.94ZM84.74,69.35h5.85v8.94h-5.85v-8.94ZM84.74,81.84h5.85v8.94h-5.85v-8.94ZM75.25,31.87h5.85v8.94h-5.85v-8.94ZM75.25,44.37h5.85v8.94h-5.85v-8.94ZM75.25,56.86h5.85v8.94h-5.85v-8.94ZM75.25,69.35h5.85v8.94h-5.85v-8.94ZM75.25,81.84h5.85v8.94h-5.85v-8.94ZM92.84,104.53h-12.84v-3.21h12.84v3.21ZM99.37,90.78h-5.85v-8.94h5.85v8.94ZM99.37,78.29h-5.85v-8.94h5.85v8.94ZM99.37,65.8h-5.85v-8.94h5.85v8.94ZM99.37,53.3h-5.85v-8.94h5.85v8.94ZM99.37,40.81h-5.85v-8.94h5.85v8.94ZM116.22,31.87h5.85v8.94h-5.85v-8.94ZM116.22,44.37h5.85v8.94h-5.85v-8.94ZM116.22,56.86h5.85v8.94h-5.85v-8.94ZM116.22,69.35h5.85v8.94h-5.85v-8.94ZM116.22,81.84h5.85v8.94h-5.85v-8.94ZM112.79,90.78h-5.85v-8.94h5.85v8.94ZM112.79,78.29h-5.85v-8.94h5.85v8.94ZM112.79,65.8h-5.85v-8.94h5.85v8.94ZM112.79,53.3h-5.85v-8.94h5.85v8.94ZM112.79,40.81h-5.85v-8.94h5.85v8.94ZM125.88,104.53h-12.84v-3.21h12.84v3.21ZM131.73,90.78h-5.85v-8.94h5.85v8.94ZM131.73,78.29h-5.85v-8.94h5.85v8.94ZM131.73,65.8h-5.85v-8.94h5.85v8.94ZM131.73,53.3h-5.85v-8.94h5.85v8.94ZM131.73,40.81h-5.85v-8.94h5.85v8.94Z" />
                        </g>
                    </svg>
                );

            case 2: // Tall thin building + small annex
                return (
                    <svg viewBox="0 0 120.93 113.1" className={className} style={style}>
                        {/* Main Structure Path */}
                        <path fill={c.fill} d="M114.45,104.26V7.86h-54.17v96.4h-6.6v8.84h67.26v-8.84h-6.49ZM94.7,107.46h-14.8v-12.06h14.8v12.06ZM108.64,89.75h-43.7v-5.12h43.7v5.12ZM108.64,80.65h-43.7v-5.12h43.7v5.12ZM108.64,71.55h-43.7v-5.12h43.7v5.12ZM108.64,62.46h-43.7v-5.12h43.7v5.12ZM108.64,53.36h-43.7v-5.12h43.7v5.12ZM108.64,44.26h-43.7v-5.12h43.7v5.12ZM108.64,35.17h-43.7v-5.12h43.7v5.12ZM108.64,26.07h-43.7v-5.12h43.7v5.12Z" />

                        {/* Annex Base */}
                        <rect fill={c.roof} x="11.35" y="44.05" width="49.27" height="66.99" />
                        {/* Windows */}
                        <rect fill={c.window} x="16.7" y="59.87" width="10.98" height="10.98" />
                        <rect fill={c.window} x="44.29" y="59.87" width="10.97" height="10.98" />
                        <rect fill={c.window} x="30.5" y="59.87" width="10.97" height="10.98" />
                        <rect fill={c.window} x="16.7" y="74.29" width="10.98" height="10.98" />
                        <rect fill={c.window} x="44.29" y="74.29" width="10.97" height="10.98" />
                        <rect fill={c.window} x="30.5" y="74.29" width="10.97" height="10.98" />
                        <rect fill={c.window} x="16.7" y="88.71" width="10.98" height="10.97" />
                        <rect fill={c.window} x="44.29" y="88.71" width="10.97" height="10.97" />
                        <rect fill={c.window} x="30.5" y="88.71" width="10.97" height="10.97" />

                        {/* Annex Roof */}
                        <g>
                            <rect fill={c.fill} x="19.58" y="16.39" width="32.81" height="28.15" />
                            <polygon fill={c.fill} points="19.6 16.39 36 0 52.39 16.39 19.6 16.39" />
                        </g>
                        {/* Small Windows */}
                        <rect fill={c.window} x="36.94" y="22.2" width="7.34" height="7.34" />
                        <rect fill={c.window} x="27.72" y="22.2" width="7.34" height="7.34" />
                        <rect fill={c.window} x="36.94" y="31.84" width="7.34" height="7.34" />
                        <rect fill={c.window} x="27.72" y="31.84" width="7.34" height="7.34" />

                        {/* Bottom Stripe */}
                        <rect fill={c.shadow} y="109.04" width="73.88" height="4" />
                    </svg>
                );

            case 3: // Blocky apartment
                return (
                    <svg viewBox="0 0 92.96 91.73" className={className} style={style}>
                        {/* Grid Frame */}
                        <path fill={c.fill} d="M71.06,81.79v-8.64h-7.36v-6.01h7.36v-8.64h-7.36v-6.76h7.36v-8.64h-7.36v-6.37h7.36v-8.64h-7.36v-5.63h29.26v68.93h-29.26v-9.59h7.36ZM78.33,81.79h8.91v-8.64h-8.91v8.64ZM78.33,67.14h8.91v-8.64h-8.91v8.64ZM78.33,51.74h8.91v-8.64h-8.91v8.64ZM78.33,36.73h8.91v-8.64h-8.91v8.64Z" />
                        {/* Main Block */}
                        <rect fill={c.roof} y="8.39" width="63.7" height="83.34" />
                        <rect fill={c.roof} width="63.7" height="6.13" />
                        {/* Windows */}
                        <rect fill={c.window} x="6.95" y="24.95" width="13.73" height="13.73" />
                        <rect fill={c.window} x="25.2" y="24.95" width="13.73" height="13.73" />
                        <rect fill={c.window} x="43.45" y="24.95" width="13.73" height="13.73" />
                        <rect fill={c.window} x="6.95" y="43.2" width="13.73" height="13.73" />
                        <rect fill={c.window} x="25.2" y="43.2" width="13.73" height="13.73" />
                        <rect fill={c.window} x="43.45" y="43.2" width="13.73" height="13.73" />
                        <rect fill={c.window} x="6.95" y="61.45" width="13.73" height="13.73" />
                        <rect fill={c.window} x="25.2" y="61.45" width="13.73" height="13.73" />
                        <rect fill={c.window} x="43.45" y="61.45" width="13.73" height="13.73" />
                    </svg>
                );

            case 4: // Small house / structure
                return (
                    <svg viewBox="0 0 87.14 83.38" className={className} style={style}>
                        <g>
                            <rect fill={c.fill} x="6.43" y="34.06" width="40.43" height="49.32" />
                            <polygon fill={c.fill} points="26.64 22.39 6.43 34.06 26.64 34.06 46.85 34.06 26.64 22.39" />
                            {/* Antenna/Detail */}
                            <g>
                                <rect fill={c.shadow} x="37.23" y="9.5" width="3.26" height="30.55" transform="translate(-2.02 46.04) rotate(-60)" />
                                <rect fill={c.shadow} x="-1.23" y="23.14" width="30.55" height="3.26" transform="translate(-10.5 10.34) rotate(-30)" />
                            </g>
                            <rect fill={c.fill} x="1.72" y="79.45" width="49.47" height="3.89" />
                            <rect fill={c.window} x="15.3" y="37.97" width="9.5" height="9.5" />
                            <rect fill={c.window} x="28.11" y="37.97" width="9.5" height="9.5" />
                            <rect fill={c.window} x="28.11" y="50.78" width="9.5" height="9.5" />
                            <rect fill={c.window} x="15.3" y="50.78" width="9.5" height="9.5" />
                            <rect fill={c.window} x="28.11" y="63.58" width="9.5" height="9.5" />
                            <rect fill={c.window} x="15.3" y="63.58" width="9.5" height="9.5" />
                        </g>
                        <g>
                            <rect fill={c.roof} x="46.85" width="40.3" height="82.91" />
                            <rect fill={c.window} x="71.4" y="9.23" width="10.35" height="10.35" />
                            <rect fill={c.window} x="71.4" y="27.26" width="10.35" height="10.35" />
                            <rect fill={c.window} x="71.4" y="45.3" width="10.35" height="10.35" />
                            <rect fill={c.window} x="71.4" y="63.33" width="10.35" height="10.35" />
                            <rect fill={c.window} x="52.24" y="9.23" width="10.35" height="10.35" />
                            <rect fill={c.window} x="52.24" y="27.26" width="10.35" height="10.35" />
                            <rect fill={c.window} x="52.24" y="45.3" width="10.35" height="10.35" />
                            <rect fill={c.window} x="52.24" y="63.33" width="10.35" height="10.35" />
                        </g>
                    </svg>
                );
        }
    };

    return renderPaths() || null;
};

export default BuildingIcon;
